import { listSimLaunches } from '@/lib/launchpad/store'
import type { LaunchState } from '@/lib/launchpad/types'
import { weiToEthNumber } from '@/lib/launchpad/wei'
import { listSimPresales } from '@/lib/presale/store'
import type { SimPresale } from '@/lib/presale/types'
import { tokenSharePath } from '@/lib/shareSlug'

export const RECENT_ACTIVITY_LIMIT = 8

export type RecentKind = 'launch' | 'presale'

export interface RecentToken {
  id: string
  kind: RecentKind
  name: string
  ticker: string
  imageUrl?: string
  contractAddress?: string
  shareSlug?: string
  tokenAddress?: string
  href: string
  amountEth: number
  atMs: number
}

function toIsoMs(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value))
    return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function recentFromLaunch(launch: LaunchState): RecentToken | undefined {
  if (launch.realQuoteRaised <= 0n && launch.tokensSold <= 0n)
    return undefined
  return {
    id: launch.id,
    kind: 'launch',
    name: launch.name,
    ticker: launch.ticker,
    imageUrl: launch.imageUrl,
    contractAddress: launch.contractAddress,
    shareSlug: launch.shareSlug,
    tokenAddress: launch.tokenAddress || launch.contractAddress,
    href: tokenSharePath(launch.shareSlug, launch.id),
    amountEth: weiToEthNumber(launch.realQuoteRaised),
    atMs: launch.lastTradeAtMs ?? launch.createdAtMs,
  }
}

function recentFromPresale(presale: SimPresale): RecentToken | undefined {
  if (presale.raisedWei <= 0n)
    return undefined
  return {
    id: presale.id,
    kind: 'presale',
    name: presale.name,
    ticker: presale.ticker,
    imageUrl: presale.imageUrl,
    contractAddress: presale.contractAddress,
    shareSlug: presale.shareSlug,
    tokenAddress: presale.tokenAddress,
    href: tokenSharePath(presale.shareSlug, presale.id),
    amountEth: weiToEthNumber(presale.raisedWei),
    atMs: presale.lastContributionAtMs ?? toIsoMs(presale.createdAt),
  }
}

function rankRecent(rows: Array<RecentToken | undefined>) {
  return rows
    .filter((row): row is RecentToken => Boolean(row && row.atMs > 0))
    .sort((a, b) => b.atMs - a.atMs)
    .slice(0, RECENT_ACTIVITY_LIMIT)
}

export async function loadRecentLaunches(): Promise<RecentToken[]> {
  return rankRecent(listSimLaunches().map(recentFromLaunch))
}

export async function loadRecentPresales(): Promise<RecentToken[]> {
  return rankRecent(listSimPresales().map(recentFromPresale))
}
